
import { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface PaymentFormProps {
  onSuccess: (paymentMethodId: string) => void;
  onError: (error: Error) => void;
  planType: string;
  loading?: boolean;
}

export const PaymentForm = ({ onSuccess, onError, planType, loading }: PaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [processing, setProcessing] = useState(false);

  // Map price IDs to human-readable plan names
  const getPlanNameFromPriceId = (priceId: string): string => {
    const planMap: Record<string, string> = {
      "price_1QvOaiG3RRUf49cQgmkP8MtT": "Monthly",
      "price_1QvObYG3RRUf49cQp6XPRkNN": "Yearly",
      "price_1QvOcGG3RRUf49cQlU0O22em": "Lifetime"
    };
    
    return planMap[priceId] || "Selected";
  };

  const planName = getPlanNameFromPriceId(planType);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      onError(new Error("Please enter your full name"));
      return;
    }

    setProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: `${firstName} ${lastName}`,
        },
      });

      if (error) {
        throw error;
      }

      if (!paymentMethod) {
        throw new Error("Payment method creation failed");
      }

      onSuccess(paymentMethod.id);
    } catch (error: any) {
      console.error("Payment error:", error);
      onError(error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            disabled={processing || loading}
            // Removed autofocus to prevent keyboard pop-up on mobile
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            disabled={processing || loading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Card Details</Label>
        <div className="border rounded-md p-3 bg-white">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={!stripe || processing || loading}
        className="w-full"
      >
        {processing || loading ? "Processing..." : `Subscribe to ${planName} plan`}
      </Button>
    </form>
  );
};
